import { useCallback, useEffect, useRef, useState } from 'react';

import {
  KeyboardEventType,
  MouseEventType,
  TouchEventType,
  WindowEventType,
} from '@this+that/utils';

import { createSession } from '../services/userSession.service';

interface UseInactivityTrackerReturn {
  isNewSession: boolean;
  lastActivity: Date | null;
}

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = 'lastActivity';
const ACTIVITY_DEBOUNCE_MS = 5000;
const DEBOUNCE_INTERVAL_MS = 1000;
const STORAGE_CHECK_INTERVAL_MS = 500;

const ACTIVITY_EVENTS = [
  MouseEventType.MOUSEDOWN,
  KeyboardEventType.KEYDOWN,
  MouseEventType.CLICK,
  TouchEventType.TOUCHSTART,
] as const;

export function useInactivityTracker(): UseInactivityTrackerReturn {
  const [isNewSession, setIsNewSession] = useState(false);
  const [lastActivity, setLastActivity] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<Date | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [storageKey, setStorageKey] = useState(0);
  const hasCheckedStorageRef = useRef(false);

  const updateLastActivity = useCallback(() => {
    const now = new Date();
    lastActivityRef.current = now;
    setLastActivity(now);
    sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, now.toISOString());
  }, []);

  const createNewSession = useCallback(async () => {
    try {
      await createSession();
      setIsNewSession(true);
      updateLastActivity();
    } catch (error) {
      console.error('Failed to create new session:', error);
    }
  }, [updateLastActivity]);

  const resetInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      createNewSession();
    }, INACTIVITY_TIMEOUT_MS);
  }, [createNewSession]);

  const handleActivity = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const now = Date.now();
      const lastUpdate = lastActivityRef.current?.getTime() || 0;

      if (now - lastUpdate > ACTIVITY_DEBOUNCE_MS) {
        updateLastActivity();
        resetInactivityTimer();
      }
    }, DEBOUNCE_INTERVAL_MS);
  }, [updateLastActivity, resetInactivityTimer]);

  const checkStoredActivity = useCallback(async () => {
    try {
      const storedActivity = sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
      hasCheckedStorageRef.current = true;

      if (!storedActivity) {
        await createNewSession();
        return;
      }

      const lastActivityDate = new Date(storedActivity);
      const now = new Date();
      const timeDiff = now.getTime() - lastActivityDate.getTime();

      if (timeDiff > INACTIVITY_TIMEOUT_MS) {
        await createNewSession();
      } else {
        lastActivityRef.current = lastActivityDate;
        setLastActivity(lastActivityDate);
        resetInactivityTimer();
      }
    } catch (error) {
      console.error('Error checking stored activity:', error);
      await createNewSession();
    }
  }, [createNewSession, resetInactivityTimer]);

  useEffect(() => {
    checkStoredActivity();
  }, [checkStoredActivity, storageKey]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_STORAGE_KEY && e.newValue === null) {
        setStorageKey((prev) => prev + 1);
      }
    };

    let previousValue = sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);

    const checkStorageInterval = setInterval(() => {
      const currentValue = sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);

      if (previousValue && !currentValue) {
        setStorageKey((prev) => prev + 1);
      }

      previousValue = currentValue;
    }, STORAGE_CHECK_INTERVAL_MS);

    const handleWindowFocus = () => {
      const currentValue = sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
      if (!currentValue && hasCheckedStorageRef.current) {
        setStorageKey((prev) => prev + 1);
      }
    };

    window.addEventListener(WindowEventType.STORAGE, handleStorageChange);
    window.addEventListener(WindowEventType.FOCUS, handleWindowFocus);

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearInterval(checkStorageInterval);
      window.removeEventListener(WindowEventType.STORAGE, handleStorageChange);
      window.removeEventListener(WindowEventType.FOCUS, handleWindowFocus);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isNewSession,
    lastActivity,
  };
}
